import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">请检查链接是否正确</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://powenxue.com/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'po文学指南',
    },
    articleSection: category?.name || '',
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: { '@id': 'https://powenxue.com', name: '首页' },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: { '@id': `https://powenxue.com/category/${article.category}`, name: category?.name || '' },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: { name: article.title },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{article.title} - po文学指南</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={article.tags.join(',')} />
        <link rel="canonical" href={`https://powenxue.com/article/${article.slug}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://powenxue.com/article/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={article.imageUrl} />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={category?.name || ''} />
        {article.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.imageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category?.name || '', href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="mt-8 mb-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {article.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </header>

        <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="article-content max-w-none"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-12 pt-8 border-t border-background-200/60">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500">分享文章：</span>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="分享到微博">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="分享到微信">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="复制链接">
              <i className="ri-link"></i>
            </button>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                      {ra.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}